import { MouseEvent, MouseEventHandler, useEffect, useState } from 'react';
import { useMapsLibrary, useMap} from '@vis.gl/react-google-maps'
import { config, geoCodeRestrictions } from '../../config';

import PlaceAutocomplete from './PlaceAutoComplete';
import GetLocationMarker from './icon';
import GeoError from './GeoError';
import SearchSetProps from '../../props/SearchSetProps';
import './search.scss'

type SearchProps = {
  setUserLatLng: (latlng: google.maps.LatLngLiteral | null) => void
  setUserSetByGeo: (flag: boolean) => void
  setUserSetBySearch: (flag: boolean) => void
  setUserBounds: (latlng: google.maps.LatLngBounds) => void
  setUserSetType: (type: SearchSetProps) => void
  initialBounds: google.maps.LatLngBounds | undefined
};

const Search = ({
  setUserLatLng,
  setUserSetByGeo,
  setUserSetBySearch,
  setUserBounds,
  setUserSetType,
  initialBounds
}:SearchProps) => {
  const [hasGeoLocation, setHasGeoLocation] = useState(false)
  const [windowIsSecureContext, setWindowIsSecureContext] = useState(false)
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);
  const geocodingLib = useMapsLibrary('geocoding')
  const [geoError, setGeoError] = useState<string | null>(null)
  const [isGeoError, setIsGeoError] = useState<boolean>(false)
  const map = useMap()
  const [clearFlag, setClearFlag] = useState<boolean>(false)

  const getUserByGeoAPI = (placename:string) => {
    // this function only comes into play when the user has entered something manually, bypassing the autocomplete.
    if (!geocodingLib) return
    const geocoder = new geocodingLib.Geocoder()
    geoCodeRestrictions.address = placename
    geoCodeRestrictions.bounds = initialBounds
    geocoder.geocode(geoCodeRestrictions, (results, status:string) => {
      if (status !== 'OK') {
        setGeoError(status)
        setIsGeoError(true)
        console.error('something went wrong: ', status)
        return
      }

      if (!results) return

      setUserBounds(results[0].geometry.viewport)
      setUserLatLng({lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng()})
      setUserSetByGeo(false)
      setUserSetBySearch(true)
      setIsGeoError(false)
      setUserSetType({type: ['locality', 'political'].some(l => results[0].types.includes(l)) ? 'suburb' : 'address'})
    })
  }

  useEffect(() => {
    if ('geolocation' in navigator) {
      setHasGeoLocation(true)
    }
    setWindowIsSecureContext(window.isSecureContext)
  }, [])

  const resetLocation:MouseEventHandler = (evt) => {
    evt.preventDefault()
    setUserLatLng(null)
    setUserSetByGeo(false)
    setUserSetBySearch(false)
    setSelectedPlace(null)
    setIsGeoError(false)
    setUserSetType({type: null})
    setClearFlag(true)
    map?.setCenter(config.map.center)
    map?.setZoom(config.map.zoom)
    return false
  }

  useEffect(() => {
    if (!selectedPlace) return
    if (selectedPlace?.geometry?.location ) {
      setUserLatLng({lat: selectedPlace.geometry.location.lat(), lng: selectedPlace.geometry.location.lng()})
      setUserSetBySearch(true)
      setUserSetByGeo(false)
      setIsGeoError(false)
      const spl = selectedPlace?.geometry?.viewport
      if (spl) setUserBounds(spl)
      setUserSetType({type: ['locality', 'political'].some(l => selectedPlace.types?.includes(l)) ? 'suburb' : 'address'})
    } else if (selectedPlace?.name) {
      // we should only be in here if the user entered something into the text box without using the autocomplete.
      getUserByGeoAPI(selectedPlace.name)
    }
  }, [selectedPlace])

  const getGeoLocationFromBrowser:MouseEventHandler = (evt:MouseEvent) => {
    // Note: this method of user location does not return a suburb, so it is not possible to use map.fitBounds() to zoom in to the map once a selection has been made.
    // To get the above functionality to work would require a new call to the maps API.

    if (!evt) return
    evt.preventDefault()

    if (hasGeoLocation) {
      navigator.geolocation.getCurrentPosition(loc => {
				const { latitude, longitude } = loc.coords;
				setUserLatLng({lat: latitude, lng:longitude});
        setUserSetByGeo(true)
        setUserSetBySearch(false)
        setIsGeoError(false)
        setUserSetType({type: 'browser'})
			})
    }

    return false
  }

  return (
    <div className='nsw-form nsw-m-top-md'>
      <div className='nsw-form__group'>
        <PlaceAutocomplete onPlaceSelect={setSelectedPlace} setClearFlag={setClearFlag} clearFlag={clearFlag} initialBounds={initialBounds}/>
        <GeoError message={geoError} isError={isGeoError}/>
        <span className='hidden' id={`${config.namespace}-location-search-help`}>Enter your suburb or postcode to search for recycling options in your area.</span>
        <div className='crc-location__links nsw-display-flex nsw-justify-content-between'>
          {
            windowIsSecureContext &&
            hasGeoLocation &&
            <a href='#' className={`${config.namespace}-location-link`} onClick={getGeoLocationFromBrowser}><GetLocationMarker /> Use my current location</a>
          }
          <a href='#' className={`${config.namespace}-location-link`} onClick={resetLocation}><span className='crc-location-link-image' aria-hidden={true}><svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor"><g><path d="M0,0h24v24H0V0z" fill="none"/></g><g><g><path d="M6,13c0-1.65,0.67-3.15,1.76-4.24L6.34,7.34C4.9,8.79,4,10.79,4,13c0,4.08,3.05,7.44,7,7.93v-2.02 C8.17,18.43,6,15.97,6,13z M20,13c0-4.42-3.58-8-8-8c-0.06,0-0.12,0.01-0.18,0.01l1.09-1.09L11.5,2.5L8,6l3.5,3.5l1.41-1.41 l-1.08-1.08C11.89,7.01,11.95,7,12,7c3.31,0,6,2.69,6,6c0,2.97-2.17,5.43-5,5.91v2.02C16.95,20.44,20,17.08,20,13z"/></g></g></svg></span> Reset location information</a>
        </div>
      </div>
    </div>
  )
}

export default Search

